import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { CONTACT, SERVICE_OPTIONS } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteForm = () => {
  const [form, setForm] = useState({ nombre: "", telefono: "", servicio: "", mensaje: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.telefono.trim()) {
      toast.error("Por favor, indica tu nombre y teléfono.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        servicio: form.servicio || "No especificado",
      });
      setDone(true);
      toast.success("¡Solicitud enviada! Te contactaré lo antes posible.");
      setForm({ nombre: "", telefono: "", servicio: "", mensaje: "" });
    } catch (err) {
      toast.error("No se pudo enviar. Prueba por WhatsApp o llamando.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/25 py-3 text-base text-white placeholder:text-white/40 focus:outline-none focus:border-[#5AB82D] transition-colors";

  return (
    <section id="presupuesto" data-testid="quote-section" className="bg-[#0A1F10] text-white py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5AB82D]">
              Contáctenos sin compromiso
            </span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight leading-[1.05]">
              Cuéntame tu avería
            </h2>
            <p className="mt-6 text-base text-white/60 leading-relaxed max-w-md">
              Rellena el formulario o envíame una foto/vídeo de la avería por WhatsApp.
              Te doy un presupuesto claro y a precio cerrado.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5AB82D] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#4fa526] transition-colors"
              >
                <WhatsAppIcon className="w-4 h-4" /> WhatsApp directo
              </a>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white hover:border-[#5AB82D] transition-colors"
              >
                Llamar 722 29 49 23
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-start justify-center bg-white/5 border border-white/10 rounded-sm p-10"
                data-testid="quote-success"
              >
                <CheckCircle2 className="w-12 h-12 text-[#5AB82D] mb-5" />
                <h3 className="font-serif-display text-3xl font-semibold mb-2">¡Solicitud recibida!</h3>
                <p className="text-white/60 max-w-md">
                  Gracias por confiar en mí. Te contactaré lo antes posible. Si es urgente,
                  llámame directamente al 722 29 49 23.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="quote-reset-btn"
                  className="mt-6 text-sm font-bold text-[#5AB82D] hover:underline"
                >
                  Enviar otra solicitud
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-white/50">Nombre</label>
                  <input
                    data-testid="quote-nombre"
                    value={form.nombre}
                    onChange={update("nombre")}
                    placeholder="Tu nombre"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-white/50">Teléfono</label>
                  <input
                    data-testid="quote-telefono"
                    value={form.telefono}
                    onChange={update("telefono")}
                    placeholder="Tu teléfono"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-white/50">Tipo de servicio</label>
                  <select
                    data-testid="quote-servicio"
                    value={form.servicio}
                    onChange={update("servicio")}
                    className={`${inputClass} [&>option]:text-[#0A1F10]`}
                  >
                    <option value="">Selecciona una opción</option>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-white/50">Mensaje</label>
                  <textarea
                    data-testid="quote-mensaje"
                    value={form.mensaje}
                    onChange={update("mensaje")}
                    rows={3}
                    placeholder="Describe brevemente la avería"
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <motion.button
                    type="submit"
                    data-testid="quote-submit-btn"
                    disabled={loading}
                    whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5AB82D] px-8 py-4 text-base font-bold text-white shadow-[0_12px_30px_rgba(90,184,45,0.3)] hover:bg-[#4fa526] transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</>
                    ) : (
                      <><Send className="w-5 h-5" /> Solicitar presupuesto</>
                    )}
                  </motion.button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
